<template>
  <v-container>
    <v-row justify-center>
      <v-col md="10">
        <v-stepper v-model.number="step" vertical non-linear>

          <v-row v-if="isActiveRegistration" class="mt-4 mx-4">
            <v-col>
              <p>Registration for ABN: <span class="font-weight-bold">{{ registration.abn }}</span></p>
              <p>Entity name: <span class="font-weight-bold">{{ registration.entityName }}</span></p>
              <p>Selected business name: <span class="font-weight-bold">{{ registration.businessName }}</span></p>
            </v-col>
          </v-row>
          <v-stepper-step :complete="step > 1" step="1">Start</v-stepper-step>

          <v-stepper-content step="1">
            <p>
              To complete a MSA registration there are a number of steps to follow.
              Once you commence the registration process you will receive a 'MSA Registration Application' email.
              Keep this email as it will have an access link that you can use to come back and continue your registration.
              If you don't receive the 'MSA Registration Application' email, or need assistance with your registration contact MSA on 1800 111 672 or msaenquiries@mla.com.au
            </p>

            <div class="text-h5">
              New Registrations
            </div>
            <div class="font-weight-bold mt-2">
              If you are registered with Livestock Production Assurance (LPA), you will be directed to login to your myMLA account to confirm your LPA details.
              If you do not have a myMLA account, you will need to create one before commencing your MSA registration.
            </div>
            <v-btn
              color="primary"
              @click="navigateToProducerPortal"
            >
              LPA Producer - Click HERE to Register with MSA
              <v-icon>link</v-icon>
            </v-btn>

            <v-divider class="my-2"/>
            <div class="text-h5">
              All Other Registrations
            </div>
            <div>
              Please begin by providing your Australian Business Number (ABN)
            </div>
            <v-form :disabled="applicationNumber > 0">
              <abr-search-field
                v-model="abn"
                name="abn"
              />
              <v-btn
                name="abn-next"
                color="primary"
                :disabled="$apollo.queries.business.loading || !abnIsValid"
                :loading="$apollo.queries.business.loading"
                @click="step = 2"
              >
                Next
              </v-btn>
            </v-form>
            <div class="text-h5 mt-4">
              Returning Registrations
            </div>
            <v-form :disabled="abn.length > 0">
              <v-row>
                <v-col md="6">
                  <v-text-field
                    v-model.number="applicationNumber"
                    label="Application number"
                  />
                </v-col>
                <v-col md="6">
                  <v-text-field
                    v-model="accessCode"
                    ref="accessCode"
                    label="Access code"
                    :hint="accessCodeHint"
                    :disabled="!applicationNumber"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-btn
                    name="returning-registration-next"
                    color="primary"
                    :disabled="$apollo.queries.registrationByApplicationNumber.loading || !registrationByApplicationNumber._id || !authenticated"
                    :loading="$apollo.queries.registrationByApplicationNumber.loading"
                    @click="resumeRegistration"
                  >
                    Next
                  </v-btn>
                </v-col>
              </v-row>
            </v-form>
          </v-stepper-content>

          <v-stepper-step :complete="step > 2" step="2">Business Selection</v-stepper-step>

          <v-stepper-content step="2">
            <p>ABN: <span class="font-weight-bold">{{ abn }}</span></p>
            <p v-if="abnFound">Entity name: <span class="font-weight-bold">{{ business.entityName }}</span></p>

            <div v-if="nonMatchingRegistrations.length">
              <v-divider class="my-3" />
              <p class="font-weight-bold">
                Existing registrations were found for the selected ABN. If you wish to start a new registration, please click next.
              </p>
              <registration-list :items="nonMatchingRegistrations" @continue="enterAccessCode" />
            </div>

            <div v-if="business.businessNames.length">
              <p class="font-weight-bold">
                To commence the registration process, you may register as your entity name, or select one of the registered business names below.
              </p>
              <business-name-list
                v-model="businessName"
                :business-names="business.businessNames"
              />
              <div v-if="matchingRegistrations.length">
                <v-divider class="my-3" />
                <p class="font-weight-bold">
                  Existing registrations were found for the selected business. If you wish to start a new registration, please click next.
                </p>
                <registration-list :items="matchingRegistrations" @continue="enterAccessCode" />
              </div>
            </div>

            <v-text-field
              v-if="!abnFound"
              v-model="business.entityName"
              label="Entity name as registered in the ABR"
            />

            <v-btn
              name="business-select-next"
              color="primary"
              :disabled="$apollo.queries.existingRegistrations.loading || !business.entityName"
              :loading="$apollo.queries.existingRegistrations.loading"
              @click="selectBusiness"
              class="mt-3"
            >
              Next
            </v-btn>
          </v-stepper-content>

          <v-stepper-step :complete="step > 3" step="3">Contact Details</v-stepper-step>

          <v-stepper-content step="3">
            <v-card outlined tile>
              <v-card-title>Business details</v-card-title>
              <v-card-text>
                <p>Please provide the following details to help MSA identify your business.</p>

                <v-text-field
                  v-model="memberName"
                  name="member-name"
                  label="Membership name"
                  hint="This is the name that MSA will refer to your business by. You may use a property name or similar to make this distinct."
                  persistent-hint
                />
              </v-card-text>
            </v-card>

            <v-card outlined tile class="mt-5">
              <v-card-title>Primary contact details</v-card-title>
              <v-card-text>
                <p>In order for MSA to contact you and to set up your myMSA account, please provide the following details;</p>
                <person-form
                  v-model="person"
                  @valid="personValid = $event"
                />
              </v-card-text>
            </v-card>
            <v-btn
              name="contact-details-next"
              class="mt-5"
              color="primary"
              @click="saveRegistration"
              :disabled="disableInput || !personValid"
            >
              Next
            </v-btn>
          </v-stepper-content>

          <v-stepper-step :complete="step > 4" :editable="isActiveRegistration" step="4">Business Activities</v-stepper-step>

          <v-stepper-content step="4">
            <v-alert
              dense
              type="info"
              v-if="isActiveRegistration"
            >
              A registration application email has been sent{{ person.email ? ` to ${person.email}` : '' }}.
              Please keep this email, as it contains your <span class="font-weight-bold">application number and access code</span>
              which will be required to return to this registration.
            </v-alert>

            <p>Select the MSA activities that best fit your business activities.</p>
            <registration-member-type-select
              v-if="registration._id"
              :registrationId="registrationId"
              v-bubble.error
              @change="onActivitiesSelected"
            />
          </v-stepper-content>

          <v-stepper-step :complete="step > 5" :editable="isActiveRegistration" step="5">Business Details</v-stepper-step>

          <v-stepper-content step="5">
            <template v-if="registration._id">
              <v-card outlined tile class="mt-3">
                <v-card-title>
                  Addresses
                </v-card-title>
                <v-card-text class="text--primary">
                  <registration-addresses
                    :registrationId="registrationId"
                    :requiredAddressTypes="[AddressType.POSTAL, AddressType.BUSINESS]"
                    @valid="addressesValid = $event"
                    v-bubble.error
                  />
                </v-card-text>
              </v-card>

              <v-card outlined tile class="mt-3">
                <v-card-title>
                  Business Contact Details
                </v-card-title>
                <v-card-text class="text--primary">
                  <p>
                    Please provide details for MSA to contact your business. You may use the same details provided in previous steps if you do not have a business phone or email.
                  </p>
                  <registration-contact-details
                    :registrationId="registrationId"
                    :requiredContactDetailTypes="[ContactDetailType.EMAIL]"
                    @valid="contactDetailsValid = $event"
                    v-bubble.error
                  />
                </v-card-text>
              </v-card>

              <div v-for="activity of activities" :key="activity._id">
                <template v-if="activity.memberType.relevantDetails.length">
                  <v-card outlined tile class="mt-3">
                    <v-card-title>
                      {{ activity.memberType.name }} Details
                    </v-card-title>
                    <v-card-text class="text--primary">
                      <registration-activity-details-form
                        :memberType="activity.type"
                        v-model="activity.details"
                        v-bubble.error
                      />
                    </v-card-text>
                  </v-card>
                </template>
              </div>
            </template>

            <v-btn
              name="business-details-next"
              color="primary"
              class="mt-3"
              :disabled="!canSetActivityDetails || disableInput"
              @click="setActivityDetails"
            >
              Next
            </v-btn>
          </v-stepper-content>

          <v-stepper-step :complete="step > 6" :editable="isActiveRegistration" step="6">License Agreements and Assessment</v-stepper-step>

          <v-stepper-content step="6">
            <registration-activities-submit
              v-if="step === 6"
              :registrationId="registrationId"
              :disableInput="disableInput"
              @endUserAssessment="redirectToEndUserPortal"
              @producerAssessment="redirectToProducerPortal"
              v-bubble.error
              v-bubble.info
            />

            <v-btn
              name="registration-submit-done"
              color="primary"
              class="mt-3"
              :disabled="!submitted"
              @click="step = 7"
            >
              Done
            </v-btn>
          </v-stepper-content>

          <v-stepper-step :complete="step >= 7" step="7">Submitted</v-stepper-step>

          <v-stepper-content step="7">
            <p>
              Your registration has been submitted. You will be emailed details of your MSA registration soon.
            </p>
            <p v-if="trainingRequired || siteVisitRequired">
              Face-to-face training or a site visit is required for one or more of the activities you have selected. An MSA staff member will contact you for more details.
            </p>
          </v-stepper-content>
        </v-stepper>
      </v-col>
    </v-row>

    <v-row justify-center>
      <v-col md="10">
        If you need assistance with your registration contact MSA on <a :href="`tel:${HELPDESK.telephone}`">{{ HELPDESK.telephone }}</a> or <a :href="`mailto:${HELPDESK.email}`">{{ HELPDESK.email }}</a>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import gql from 'graphql-tag'
import { RegistrationStatus, AddressType, ContactDetailType } from '@mymsa/common'
import log from 'loglevel'
import { setAuth } from '../vue-apollo'
import { cloneDeep } from 'lodash'

import AbrSearchField from '../components/AbrSearchField.vue'
import BusinessNameList from '../components/BusinessNameList.vue'
import PersonForm from '../components/forms/PersonForm.vue'
import RegistrationList from '../components/RegistrationList.vue'
import RegistrationAddresses from '../components/RegistrationAddresses.vue'
import RegistrationContactDetails from '../components/RegistrationContactDetails.vue'
import RegistrationActivityDetailsForm from '../components/forms/RegistrationActivityDetailsForm.vue'
import RegistrationActivitiesSubmit from '../components/RegistrationActivitiesSubmit.vue'
import RegistrationMemberTypeSelect from '../components/RegistrationMemberTypeSelect.vue'
import {
  validateAbn,
  getEnv,
  HELPDESK,
  externalRedirect,
  externalRedirectCallback,
  generateAccessCode,
  accessCodeLength
} from '../utilities'

export default {
  name: 'RegisterView',

  components: {
    AbrSearchField,
    BusinessNameList,
    PersonForm,
    RegistrationList,
    RegistrationAddresses,
    RegistrationContactDetails,
    RegistrationActivityDetailsForm,
    RegistrationActivitiesSubmit,
    RegistrationMemberTypeSelect
  },

  props: {
    state: String,
    nonce: String
  },

  data: () => ({
    HELPDESK,
    RegistrationStatus,
    AddressType,
    ContactDetailType,
    step: 1,
    registrationId: null,
    applicationNumber: null,
    accessCode: '',
    accessCodeHint: null,
    abn: '',
    abnFound: false,
    businessName: null,
    existingRegistrations: [],
    registrationByApplicationNumber: {},
    business: {
      businessNames: [],
      entityName: null
    },
    memberName: '',
    registration: {
      activities: [],
      addresses: []
    },
    person: {},
    personValid: false,
    applyStep: false, // value of step will be set based on the query result of the registration. Used for returning to where the user left off.
    memberTypes: [],
    activities: [],
    disableInput: false,
    fetchRegistration: false,
    addressesValid: false,
    contactDetailsValid: false,
    authenticated: false
  }),

  computed: {
    abnIsValid () {
      return validateAbn(this.abn)
    },
    matchingRegistrations () {
      if (!this.selectedBusinessName) return []
      return this.existingRegistrations
        .filter(o => o.businessSearchId === this.selectedBusinessName.searchId)
    },
    nonMatchingRegistrations () {
      const needles = this.business.businessNames.map(o => o.searchId)
      return this.existingRegistrations
        .filter(o => !needles.includes(o.businessSearchId))
    },
    selectedBusinessName () {
      return this.businessName > -1
        ? this.business.businessNames[this.businessName]
        : null
    },
    isActiveRegistration () {
      return this.registration.status === RegistrationStatus.INITIAL
    },
    trainingRequired () {
      return this.registration.activities
        .some(o => o.memberType.trainingRequired)
    },
    siteVisitRequired () {
      return this.registration.activities
        .some(o => o.memberType.siteVisitRequired)
    },
    submitted () {
      return this.registration.activities
        .every(o => o.registrationStatus !== RegistrationStatus.INITIAL)
    },
    addressTypes () {
      return this.registration.addresses.map(o => o.type)
    },
    canSetActivityDetails () {
      return this.addressesValid &&
        this.contactDetailsValid
    }
  },

  apollo: {
    business: {
      query: gql`query SearchAbn($abn: String!) {
        business: businessByAbn(abn: $abn) {
          businessNames {
            businessName
            searchIdType
            searchId
          }
          entityName
        }
      }`,
      variables () {
        return {
          abn: this.abn
        }
      },
      skip () {
        return !this.abnIsValid || this.step > 1
      },
      error () {
        this.$emit('info', 'The provided ABN is valid but could not be found. Please provide more information to complete your registration.')
      },
      result ({ data }) {
        this.abnFound = !!data
        if (!data) return
        this.business = data.business
        this.step = 2
      }
    },

    registrationByApplicationNumber: {
      query: gql`query FindRegistration($filter: FilterFindOnePublicRegistrationInput!) {
        registrationByApplicationNumber: publicRegistrationGetOne(filter: $filter) {
          _id
        }
      }`,
      variables () {
        return {
          filter: {
            applicationNumber: this.applicationNumber
          }
        }
      },
      skip () {
        return !this.authenticated
      },
      error (e) {
        this.onError(e)
      },
      result ({ data }) {
        if (!data) return
        this.registrationId = data.registrationByApplicationNumber._id
      }
    },

    registration: {
      query: gql`query GetRegistration($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          abn
          entityName
          memberName
          businessName
          businessSearchId
          status
          step
          endUserAssessmentComplete
          producerAssessmentComplete
          activities {
            _id
            type
            registrationStatus
            details {
              _id
              ausMeatEstablishmentNumber
              stateEstablishmentNumber
              nlisEstablishmentNumber
              nsqaAccredited
              nlisUserId
              gs1CompanyPrefix
              gs1GlobalLocationNumber
              nfasId
              agentName
              agentNumber
              stockTypeBeef
              stockTypeSheep
              propertyIdentificationCode
              lpaUserId
              chains {
                _id
                species
                chainNumber
                chainName
                immobiliser1Enabled
                immobiliser1Value
                immobiliser2Enabled
                immobiliser2Value
                immobiliser3Enabled
                immobiliser3Value
                stimulator1Enabled
                stimulator1Value
                stimulator2Enabled
                stimulator2Value
                stimulator3Enabled
                stimulator3Value
                hidePuller1Enabled
                hidePuller1Value
                hidePuller2Enabled
                hidePuller2Value
                hidePuller3Enabled
                hidePuller3Value
              }
            }
            memberType {
              _id
              name
              trainingRequired
              siteVisitRequired
              enableEndUserPortal
              enableProducerPortal
              relevantDetails
              prerequisitesDescription
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.registrationId
        }
      },
      skip () {
        return !this.registrationId || !this.fetchRegistration || !this.authenticated
      },
      result ({ data }) {
        if (!data) return
        const { registration } = data
        this.memberName = registration.memberName || ''
        this.activities = cloneDeep(registration.activities ?? [])
      },
      error (e) {
        log.warn(e)
        this.onError(new Error('Your application number or access code is incorrect. Please try again.'))
      }
    },

    existingRegistrations: {
      query: gql`query FindRegistrations($filter: FilterFindManyPublicRegistrationInput) {
        existingRegistrations: publicRegistrationMany(filter: $filter) {
          _id
          applicationNumber
          entityName
          businessName
          memberName
          businessSearchId
          status
          memberNumber
        }
      }`,
      variables () {
        return {
          filter: {
            abn: this.abn
          }
        }
      },
      skip () {
        return !this.abn
      },
      error (e) { this.onError(e) }
    },

    memberTypes: {
      query: gql`query {
        memberTypes: memberTypeMany(limit: 0) {
          _id
          value
          trainingRequired
          siteVisitRequired
          enableEndUserPortal
          enableProducerPortal
        }
      }`,
      error (e) { this.onError(e) }
    }
  },

  methods: {
    navigateToProducerPortal () {
      window.location.href = getEnv('VUE_APP_PRODUCER_PORTAL_URL')
    },

    selectBusiness () {
      this.step = 3
      const entityName = this.business.entityName
      const businessName = this.selectedBusinessName?.businessName
      this.memberName = businessName ? `${entityName} - ${businessName}` : entityName
    },

    async saveRegistration () {
      this.disableInput = true
      try {
        if (this.registrationId) await this.save({ memberName: this.memberName })
        else await this.createNewRegistration()
      } catch (e) {
        this.onError(e)
        return
      } finally {
        this.disableInput = false
      }
      this.$apollo.queries.registration.refresh()
      this.step = 4
    },

    async createNewRegistration () {
      const accessCode = generateAccessCode()

      const res = await this.$apollo.mutate({
        mutation: gql`mutation ($registration: CreateOnePublicRegistrationInput!, $person: CreateOnePersonInput!) {
          record: publicRegistrationCreateOne(registration: $registration, person: $person) {
            _id
            applicationNumber
          }
        }`,
        variables: {
          registration: {
            registrationDate: new Date(),
            abn: this.abn,
            applicationAccessCode: accessCode,
            entityName: this.business.entityName,
            businessName: this.selectedBusinessName?.businessName,
            memberName: this.memberName,
            businessSearchId: this.selectedBusinessName?.searchId
          },
          person: this.person
        }
      })
      const { record } = res.data
      this.registrationId = record._id
      this.applicationNumber = record.applicationNumber
      this.accessCode = accessCode
    },

    async onActivitiesSelected () {
      this.$apollo.queries.registration.refresh()
      this.step = 5
    },

    redirectToEndUserPortal () {
      externalRedirect(getEnv('VUE_APP_END_USER_PORTAL_URL'), {
        applicationNumber: this.applicationNumber,
        accessCode: this.accessCode,
        registrationId: this.registrationId,
        endUser: true
      })
    },

    redirectToProducerPortal () {
      externalRedirect(getEnv('VUE_APP_NON_LPA_PRODUCER_PORTAL_URL'), {
        applicationNumber: this.applicationNumber,
        accessCode: this.accessCode,
        registrationId: this.registrationId,
        producer: true
      })
    },

    async setActivityDetails () {
      this.disableInput = true
      try {
        await this.saveActivities()
      } catch (e) {
        this.onError(e)
        return
      } finally {
        this.disableInput = false
      }
      this.$apollo.queries.registration.refresh()
      this.step = 6
    },

    async saveActivities () {
      for (const activity of this.activities) {
        const { _id, details } = activity
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $activityId: MongoID!, $record: UpdateByIdRegistrationActivitiesInput!) {
            registrationUpdateActivityById(_id: $id, activityId: $activityId, record: $record) {
              _id
            }
          }`,
          variables: {
            id: this.registrationId,
            activityId: _id,
            record: { details }
          }
        })
      }
    },

    async save (data = {}) {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdRegistrationInput!) {
            registrationUpdateById(_id: $id, record: $record){
              record {
                _id
              }
            }
          }`,
          variables: {
            id: this.registrationId,
            record: data
          }
        })
      } catch (e) {
        this.onError(e)
      }
      this.$apollo.queries.registration.refresh()
    },

    async resumeRegistration () {
      this.step = this.registration.step ?? 4
    },

    enterAccessCode (applicationNumber) {
      this.abn = ''
      this.applicationNumber = applicationNumber
      this.accessCodeHint = 'Please enter your access code to resume your registration.'
      this.step = 1
      this.$nextTick(() => this.$refs.accessCode.$refs.input.focus())
    },

    async saveStep (step) {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $record: UpdateByIdRegistrationInput!) {
            registrationUpdateById(_id: $id, record: $record){
              record {
                _id
              }
            }
          }`,
          variables: {
            id: this.registrationId,
            record: { step }
          }
        })
      } catch (e) {
        this.onError(e)
      }
    },

    async onRedirectCallback () {
      try {
        const data = externalRedirectCallback(this.state, this.nonce)
        this.applicationNumber = data.applicationNumber
        this.accessCode = data.accessCode
        this.registrationId = data.registrationId
        if (data.endUser) await this.completeEndUserAssessment()
        if (data.producer) await this.save({ producerAssessmentComplete: true })
        this.step = 6
      } catch (e) {
        log.error(e)
        this.onError('Failed to resume your registration. Please enter your application number and access code.')
      }
    },

    async completeEndUserAssessment () {
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!) {
            registrationCompleteEndUserAssessment(_id: $id)
          }`,
          variables: {
            id: this.registrationId
          }
        })
      } catch (e) {
        this.onError(e)
      }
      this.$apollo.queries.registration.refresh()
    },

    onError (err) {
      this.$emit('error', err)
    }
  },

  watch: {
    accessCode (accessCode) {
      this.authenticated = false
      let auth = null
      if (this.applicationNumber && accessCode?.length >= accessCodeLength) {
        this.authenticated = true
        auth = `application-code ${window.btoa(this.applicationNumber)}.${window.btoa(accessCode)}`
      }
      setAuth(auth)
      this.$apollo.queries.registrationByApplicationNumber.refresh()
    },

    step (step) {
      if (this.registrationId && step >= 3) this.saveStep(step)
    }
  },

  async mounted () {
    setAuth(null)

    if (this.state && this.nonce) {
      await this.onRedirectCallback()
      this.$router.push(this.$route.path) // clear query params to avoid errors on refresh
    }

    this.fetchRegistration = true
    this.$apollo.queries.registration.refresh()
  }
}
</script>
